import { HttpBackend, HttpClient, HttpEventType, HttpHeaders } from "@angular/common/http";
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { App } from "@capacitor/app";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { base64ToFile, Dimensions, ImageCroppedEvent, ImageTransform } from "ngx-image-cropper";
import { ModalController, ToastController } from "@ionic/angular";
import { AnimationOptions } from "ngx-lottie";
import { AnimationItem } from "lottie-web";
import { Subscription } from "rxjs";
import { distinctUntilChanged, first } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import * as _ from "lodash";
import * as AuthActions from "../auth/store/auth.actions";
import { DataStorageService } from "../services/data-storage/data-storage.service";
import * as fromApp from "../store/app.reducer";

@Component({
  selector: "app-photo-cropper",
  templateUrl: "./photo-cropper.component.html",
  styleUrls: ["./photo-cropper.component.scss"],
})
export class PhotoCropperComponent implements OnInit, OnDestroy {
  @Input() itemType: string;
  @Input() image: any;
  @Input() showCurrentPreview: boolean = false;
  @Input() showInlineSelector: boolean = true;
  @ViewChild("fileUploadInput", { static: false })
  public fileUploadInput: ElementRef<HTMLInputElement>;

  public canvasRotation = 0;
  public containWithinAspectRatio = false;
  public currentProfileImageSrc: string = null;
  public croppedImage: string = "";
  public currentUser: any;
  public groupApp: any;
  public isBrowserPlatform: boolean = false;
  public onSaveDisable: boolean = false;
  public options: AnimationOptions = {
    path: "https://assets3.lottiefiles.com/packages/lf20_gzoqde3x.json",
  };
  public rotation = 0;
  public scale = 1;
  public selectedImageFile: File = null;
  public showCropper = false;
  public transform: ImageTransform = {};
  public uploadProgress: number = null;
  public uploadsProgressValues = {};
  public windowDimensions: any;

  private appRestoredListener: any;
  private http: HttpClient;
  private moToken: any;
  private subscriptions: Subscription[] = [];
  private toast: any;

  constructor(
    private store: Store<fromApp.AppState>,
    private dataStorageService: DataStorageService,
    private modalController: ModalController,
    private toastController: ToastController,
    private translateService: TranslateService,
    handler: HttpBackend
  ) {
    this.http = new HttpClient(handler);
  }

  async ngOnInit() {
    this.subscriptions.push(
      this.store
        .select("windowInfo")
        .pipe(distinctUntilChanged())
        .subscribe((state) => {
          if (
            state &&
            state.windowDimensions &&
            !_.isEqual(this.windowDimensions, state.windowDimensions)
          ) {
            this.windowDimensions = _.cloneDeep(state.windowDimensions);
          }
        })
    );

    this.subscriptions.push(
      this.store
        .select("auth")
        .pipe(distinctUntilChanged())
        .subscribe((state) => {
          if (state && state.user && !_.isEqual(this.currentUser, state.user)) {
            this.currentUser = _.cloneDeep(state.user);
            this.syncCurrentProfileImage();
          }

          if (state && state.mo_token && !_.isEqual(this.moToken, state.mo_token)) {
            this.moToken = _.cloneDeep(state.mo_token);
          }
        })
    );

    this.subscriptions.push(
      this.store
        .select("groupApp")
        .pipe(distinctUntilChanged())
        .subscribe((state) => {
          if (
            state &&
            state.groupApp &&
            !_.isEqual(this.groupApp, state.groupApp)
          ) {
            this.groupApp = _.cloneDeep(state.groupApp);
            this.syncCurrentProfileImage();
          }
        })
    );

    this.subscriptions.push(
      this.store
        .select("platformInfo")
        .pipe(distinctUntilChanged())
        .subscribe((state) => {
          this.isBrowserPlatform = !!(state && state.browserPlatform);
        })
    );

    this.appRestoredListener = await App.addListener(
      "appRestoredResult",
      async (result: any) => {
        if (
          result &&
          result.pluginId === "Camera" &&
          result.data &&
          result.data.webPath
        ) {
          await this.loadCapacitorPhoto(result.data);
        }
      }
    );
  }

  onFileChanged(event) {
    const file =
      event && event.target && event.target.files ? event.target.files[0] : null;

    if (!file) {
      return;
    }

    if (!this.isSupportedImageType(file.type)) {
      void this.presentErrorToast(
        this.translateService.instant("account-settings.unsupported-image-file"),
        "danger"
      );
      event.target.value = "";
      return;
    }

    this.resetSelectedImage();
    this.selectedImageFile = file;
    event.target.value = "";
  }

  openImageSelector() {
    if (this.isBrowserPlatform) {
      if (this.fileUploadInput && this.fileUploadInput.nativeElement) {
        this.fileUploadInput.nativeElement.click();
      }

      return;
    }

    void this.selectImageFromDevice();
  }

  async selectImageFromDevice() {
    if (await this.shouldShowComingSoonForIosProfileImage()) {
      void this.presentErrorToast(
        this.translateService.instant(
          "account-settings.profile-photo-coming-soon-ios"
        ),
        "warning"
      );
      return;
    }

    try {
      const photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        promptLabelCancel: this.translateService.instant("cancel"),
        promptLabelHeader: this.translateService.instant(
          "account-settings.photo-source-title"
        ),
        promptLabelPhoto: this.translateService.instant(
          "account-settings.from-photo"
        ),
        promptLabelPicture: this.translateService.instant(
          "account-settings.take-picture"
        ),
        presentationStyle: "popover",
        resultType: CameraResultType.Uri,
        source: CameraSource.Prompt,
        correctOrientation: true,
      });

      await this.loadCapacitorPhoto(photo);
    } catch (error) {
      if (this.isUserCancelled(error)) {
        return;
      }

      void this.presentErrorToast(
        this.translateService.instant(
          "account-settings.profile-photo-permission-denied"
        ),
        "danger"
      );
    }
  }

  imageLoaded() {
    this.showCropper = true;
  }

  animationCreated(animationItem: AnimationItem) {
    animationItem.setSpeed(0.5);
    animationItem.loop = false;
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImage = event.base64 || "";
  }

  cropperReady(sourceImageDimensions: Dimensions) {}

  async uploadImageData(imgBlob, item, fileType, filename) {
    this.http
      .put(item.signedUrl, imgBlob, {
        reportProgress: true,
        observe: "events",
        headers: new HttpHeaders({
          "Content-Type": fileType,
        }),
      })
      .subscribe({
        next: async (events) => {
          if (events.type === HttpEventType.UploadProgress) {
            this.uploadsProgressValues[filename] =
              parseFloat(
                Math.floor((events.loaded / events.total) * 100).toString()
              ) / 100;
            this.uploadProgress = this.uploadsProgressValues[filename];
          } else if (events.type === HttpEventType.Response) {
            this.onSaveDisable = false;

            if (this.uploadsProgressValues[filename]) {
              delete this.uploadsProgressValues[filename];
            }

            if (events.status === 200) {
              this.uploadProgress = null;
              const updatedUser = _.cloneDeep(this.currentUser);

              if (updatedUser) {
                if (!updatedUser.profile_image) {
                  updatedUser.profile_image = {};
                }

                updatedUser.profile_image.filename = filename;
                updatedUser.profile_image.optimized = true;
                this.store.dispatch(new AuthActions.SetUser(updatedUser));
              }

              this.modalController.dismiss({ updated: true });
            } else {
              this.uploadProgress = null;
              void this.presentErrorToast(
                this.translateService.instant(
                  "errorMessages.problem_reaching_server"
                ),
                "danger"
              );
            }
          }
        },
        error: () => {
          this.onSaveDisable = false;

          if (this.uploadsProgressValues[filename]) {
            delete this.uploadsProgressValues[filename];
          }

          this.uploadProgress = null;
          void this.presentErrorToast(
            this.translateService.instant("errorMessages.problem_reaching_server"),
            "danger"
          );
        },
      });
  }

  loadImageFailed() {
    void this.presentErrorToast(
      this.translateService.instant("account-settings.failed-loading-image"),
      "danger"
    );
  }

  resetImage() {
    this.scale = 1;
    this.rotation = 0;
    this.canvasRotation = 0;
    this.transform = {};
  }

  zoomOut() {
    this.scale -= 0.1;
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  zoomIn() {
    this.scale += 0.1;
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  saveImage() {
    if (!this.croppedImage || !this.currentUser || !this.groupApp) {
      return;
    }

    this.onSaveDisable = true;

    const file: any = base64ToFile(this.croppedImage);
    const fileType = this.getUploadFileType(file.type);

    if (!fileType) {
      this.onSaveDisable = false;
      void this.presentErrorToast(
        this.translateService.instant("account-settings.unsupported-image-file"),
        "danger"
      );
      return;
    }

    const fileName = uuidv4() + ".jpg";
    const directory =
      this.groupApp.group + "/" + this.currentUser.customer_id + "/" + fileName;

    this.dataStorageService.handleImageUpload(fileName, directory).subscribe({
      next: (res: any) => {
        if (res && res.success) {
          this.uploadImageData(file, res.data, file.type, fileName);
        } else {
          this.onSaveDisable = false;
          void this.presentErrorToast(
            res && res.comments
              ? res.comments
              : this.translateService.instant(
                  "errorMessages.problem_reaching_server"
                ),
            "danger"
          );
        }
      },
      error: () => {
        this.onSaveDisable = false;
        void this.presentErrorToast(
          this.translateService.instant("errorMessages.problem_reaching_server"),
          "danger"
        );
      },
    });
  }

  private async loadCapacitorPhoto(photo) {
    if (!photo || !photo.webPath) {
      void this.presentErrorToast(
        this.translateService.instant("account-settings.failed-loading-image"),
        "danger"
      );
      return;
    }

    try {
      const response = await fetch(photo.webPath);
      const blob = await response.blob();
      const mimeType = this.resolveMimeType(blob.type, photo.format);

      if (!this.isSupportedImageType(mimeType)) {
        void this.presentErrorToast(
          this.translateService.instant("account-settings.unsupported-image-file"),
          "danger"
        );
        return;
      }

      this.resetSelectedImage();
      this.selectedImageFile = new File(
        [blob],
        "profile-photo-" + Date.now() + "." + this.getFileExtension(mimeType),
        {
          type: mimeType,
        }
      );
    } catch (error) {
      void this.presentErrorToast(
        this.translateService.instant("account-settings.failed-loading-image"),
        "danger"
      );
    }
  }

  private getFileExtension(mimeType) {
    if (mimeType === "image/png") {
      return "png";
    }

    if (mimeType === "image/webp") {
      return "webp";
    }

    return "jpg";
  }

  private getUploadFileType(mimeType) {
    if (mimeType === "image/png") {
      return "png";
    }

    if (mimeType === "image/webp") {
      return "webp";
    }

    if (mimeType === "image/jpeg") {
      return "jpg";
    }

    return null;
  }

  private isSupportedImageType(mimeType) {
    return _.includes(["image/jpeg", "image/png", "image/webp"], mimeType);
  }

  private isUserCancelled(error) {
    if (!error) {
      return false;
    }

    const message =
      _.isString(error) ? error.toLowerCase() : JSON.stringify(error).toLowerCase();

    return (
      message.includes("cancel") ||
      message.includes("canceled") ||
      message.includes("cancelled")
    );
  }

  private async shouldShowComingSoonForIosProfileImage() {
    if (!(Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios")) {
      return false;
    }

    try {
      const appInfo = await App.getInfo();
      return this.compareVersions(appInfo.version, "8.2.0") < 0;
    } catch (error) {
      return false;
    }
  }

  private compareVersions(currentVersion, targetVersion) {
    const currentParts = _.map(currentVersion.split("."), (part) =>
      parseInt(part, 10) || 0
    );
    const targetParts = _.map(targetVersion.split("."), (part) =>
      parseInt(part, 10) || 0
    );
    const maxLength = Math.max(currentParts.length, targetParts.length);

    for (let index = 0; index < maxLength; index++) {
      const currentPart = currentParts[index] || 0;
      const targetPart = targetParts[index] || 0;

      if (currentPart > targetPart) {
        return 1;
      }

      if (currentPart < targetPart) {
        return -1;
      }
    }

    return 0;
  }

  private async presentErrorToast(message, color) {
    if (this.toast) {
      await this.toast.dismiss();
      this.toast = null;
    }

    this.toast = await this.toastController.create({
      message: message,
      duration: 3000,
      position: "middle",
      color: color,
      buttons: [
        {
          side: "end",
          icon: "assets/ionicons/close.svg",
          handler: () => {},
        },
      ],
    });

    await this.toast.present();
    this.toast.onDidDismiss().then(() => {
      this.toast = null;
    });
  }

  private refreshCurrentUserState() {
    return new Promise<void>((resolve) => {
      if (!this.moToken || !this.groupApp) {
        resolve();
        return;
      }

      this.dataStorageService
        .get_user_data(
          this.moToken,
          this.groupApp.active_loyalty_app,
          this.groupApp.contestSystemEnabled,
          this.groupApp.migrating_points_from_group
        )
        .pipe(first())
        .subscribe({
          next: (res: any) => {
            if (res && res.success && res.user) {
              this.store.dispatch(new AuthActions.SetUser(_.cloneDeep(res.user)));
            }

            resolve();
          },
          error: () => {
            resolve();
          },
        });
    });
  }

  private resetSelectedImage() {
    this.croppedImage = "";
    this.selectedImageFile = null;
    this.showCropper = false;
    this.uploadProgress = null;
    this.resetImage();
  }

  private resolveMimeType(blobType, format) {
    if (this.isSupportedImageType(blobType)) {
      return blobType;
    }

    if (format === "png") {
      return "image/png";
    }

    if (format === "webp") {
      return "image/webp";
    }

    return "image/jpeg";
  }

  private syncCurrentProfileImage() {
    if (
      !this.groupApp ||
      !this.groupApp.group ||
      !this.currentUser ||
      !this.currentUser.customer_id ||
      !this.currentUser.profile_image ||
      !this.currentUser.profile_image.optimized ||
      !this.currentUser.profile_image.filename
    ) {
      this.currentProfileImageSrc = null;
      return;
    }

    this.currentProfileImageSrc =
      "https://primg.deliverymanager.gr/" +
      this.groupApp.group +
      "/" +
      this.currentUser.customer_id +
      "/" +
      this.currentUser.profile_image.filename;
  }

  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    }

    if (this.appRestoredListener) {
      this.appRestoredListener.remove();
    }

    this.selectedImageFile = null;
  }
}
